// components/admin/accounting/UsersAccounting.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Users, Loader2 } from 'lucide-react';
import DeliveriesTable from './DeliveriesTable';
import StatisticsCards from './StatisticsCards';

const API_URL = process.env.REACT_APP_API_URL;

function UsersAccounting({ userId, selectedDate }) {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [selectedDate]);

  useEffect(() => {
    if (selectedUser) {
      fetchUserDeliveries(selectedUser.id, '');
    }
  }, [selectedUser, selectedDate]);

  async function fetchUsers() {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/accounting/users`, {
        params: { user_id: userId, date: selectedDate }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUserDeliveries(targetUserId, search) {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/user/${targetUserId}/deliveries`,
        {
          params: { admin_id: userId, date: selectedDate, search }
        }
      );
      setDeliveries(response.data.deliveries);
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleUserSelect(user) {
    setSelectedUser(user);
    setSearchTerm('');
  }

  function handleSearch(search) {
    if (selectedUser) {
      fetchUserDeliveries(selectedUser.id, search);
    }
  }

  function handleExport() {
    const data = {
      admin_id: userId,
      deliveries: deliveries,
      statistics: statistics,
      type: 'user',
      title: `Contabilidad - ${selectedUser?.username || 'Usuario'} - ${selectedDate}`
    };

    axios.post(`${API_URL}/api/accounting/export`, data, {
      responseType: 'blob'
    })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `contabilidad_${selectedUser?.username}_${selectedDate}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(error => {
        console.error('Error exporting:', error);
      });
  }

  if (!selectedUser) {
    return (
      <div>
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-800 font-montserrat mb-4">
            Selecciona un Usuario
          </h2>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-montserrat">
                No hay usuarios con entregas en esta fecha
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {users.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleUserSelect(user)}
                  className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-brand-primary hover:shadow-lg transition-all text-left group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-800 font-montserrat group-hover:text-brand-primary transition-colors">
                        {user.username}
                      </h3>
                      <p className="text-sm text-slate-500">{user.email}</p>
                    </div>
                    <div className="bg-brand-primary/10 text-brand-primary px-3 py-1 rounded-full text-sm font-bold font-montserrat">
                      {user.delivery_count}
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 font-montserrat">
                    {user.phone_number}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* User Header */}
      <div className="mb-6 bg-gradient-to-r from-brand-primary to-brand-primary/80 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold font-montserrat mb-1">
              {selectedUser.username}
            </h2>
            <p className="text-white/90 text-sm">{selectedUser.email}</p>
          </div>
          <button
            onClick={() => setSelectedUser(null)}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-montserrat font-semibold transition-all"
          >
            Volver
          </button>
        </div>
      </div>

      {/* Statistics */}
      {statistics && (
        <StatisticsCards statistics={statistics} type="user" />
      )}

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por ID, nombre o teléfono..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              handleSearch(e.target.value);
            }}
            className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-brand-primary transition-all font-montserrat"
          />
        </div>
      </div>

      {/* Deliveries Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
        </div>
      ) : (
        <DeliveriesTable 
          deliveries={deliveries} 
          onExport={handleExport}
          showUserColumn={false}
        />
      )}
    </div>
  );
}

export default UsersAccounting;